// One patient's statement, rendered for reading, printing or saving as a PDF.
//
// Opened from the A/R "By patient" rows and from the patient detail. NOTHING HERE
// SENDS: the HTML comes from GET …/patients/<id>/statement and is shown in a
// sandboxed iframe exactly as the Statements page shows it; "Download PDF" fetches
// the same document from …/statement/pdf and hands it to the browser as a file. A
// fetch rather than a link for the PDF, because the Clerk session token only rides
// on window.fetch.

import React, { useEffect, useRef, useState } from "react";
import { formatPatientName } from "../utils/formatName";
import "./StatementModal.css";

const API_URL = process.env.REACT_APP_API_URL || "";

// One letter page at 96 dpi. The statement is drawn at this size and scaled down to fit
// the modal, so the whole page shows at once, as the PDF does (David, 2026-10-21).
const PAGE_WIDTH = 816;
const PAGE_HEIGHT = 1056;

export default function StatementModal({
  practiceId,
  patientId,
  patientName,
  onClose,
}) {
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);

  // Fit the page to the space the modal gives it, and refit when the window resizes.
  useEffect(() => {
    const fit = () => {
      const stage = stageRef.current;
      if (!stage || !stage.clientWidth || !stage.clientHeight) return;
      setScale(Math.min(1, stage.clientWidth / PAGE_WIDTH, stage.clientHeight / PAGE_HEIGHT));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [html]);

  useEffect(() => {
    if (!practiceId || !patientId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setHtml(null);
    fetch(
      `${API_URL}/api/payments/practices/${practiceId}/patients/${patientId}/statement`,
      { credentials: "include" },
    )
      .then(async (resp) => {
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          throw new Error(
            body.error || `Could not render the statement (${resp.status})`,
          );
        }
        const body = await resp.json();
        if (cancelled) return;
        if (!body || typeof body.html !== "string") {
          throw new Error("The statement could not be read. Try again.");
        }
        setHtml(body.html);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, patientId]);

  const downloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    setError(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/payments/practices/${practiceId}/patients/${patientId}/statement/pdf`,
        { credentials: "include" },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || "Could not generate the statement");
      }
      const disposition = resp.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = match ? match[1] : "statement.pdf";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const name = formatPatientName(patientName);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content statement-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Patient Statement"
      >
        <div className="statement-modal-header">
          <h2>Patient Statement</h2>
          <div className="statement-modal-actions">
            <button
              className="btn btn-primary btn-sm"
              onClick={downloadPdf}
              disabled={downloading || loading || !!error}
            >
              {downloading ? "Generating…" : "Download PDF"}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {error && (
          <div className="statement-modal-message error">
            {error}
            <button
              className="statement-modal-message-close"
              onClick={() => setError(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {loading && (
          <p className="statement-modal-loading">Loading statement…</p>
        )}

        {/* sandbox without allow-scripts: the page is ours and carries no script,
            and the statement's own content is escaped server-side. An iframe
            rather than dangerouslySetInnerHTML keeps its stylesheet out of the
            app's. Same as features/money/StatementsPage. */}
        {html !== null && (
          <div className="statement-modal-stage" ref={stageRef}>
            <div
              className="statement-modal-page"
              style={{ width: PAGE_WIDTH * scale, height: PAGE_HEIGHT * scale }}
            >
              <iframe
                className="statement-modal-frame"
                title={`Statement for ${name}`}
                sandbox="allow-same-origin allow-modals"
                srcDoc={html}
                style={{
                  width: PAGE_WIDTH,
                  height: PAGE_HEIGHT,
                  transform: `scale(${scale})`,
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
